import { useQuery } from '@apollo/client';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Slide from '@mui/material/Slide';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { useContext, useEffect, useState } from 'react';
import {
  DARK_MAP_STYLE,
  LIGHT_MAP_STYLE,
  SATELLITE_MAP_STYLE,
} from '@/components/Map/Utils/mapStyleTypes';
import ErrorBoundary from '../../../components/ErrorBoundary';
import { DEFAULT_BOUNDARY_COLOR, WELL_COLORS } from '../../../components/Map/Utils/colors';
import {
  LABEL_FONT,
  MAP_LAYER_IDS,
  MAP_LAYER_OPTIONS,
  MAP_SOURCE_IDS,
  MAP_SOURCE_RETRY_TIMEOUT,
} from '../../../components/Map/Utils/constants';
import { getLatLongFromPoint } from '../../../utils/helpers';
import MapFilterContext from './filterContext';
import LayersTab from './layers';
import { MAP_SCREENING_EA_WELLS_QUERY } from './operations';
import ScreeningTab from './screening';
import SearchTab from './search';
import { MapSearchProvider } from './searchContext';

const useStyles = makeStyles(theme => ({
  filters: {
    ...theme.mapPanel,
    backgroundColor: 'white',
    padding: '5px',
  },
  formContainer: {
    margin: '5px',
    padding: '0 5px',
  },
  radio: {
    color: '#2a2e49',
    '&.Mui-checked': {
      color: '#2a2e49',
    },
  },
  radioGroup: {
    flexDirection: 'row',
  },
  tab: {
    backgroundColor: 'rgba(0,0,0,.2)',
    border: 'solid thin rgba(0, 0, 0, .5)',
    color: 'rgba(0, 0, 0, .7)',
    minWidth: '50px',
    height: '36px',
    minHeight: 'unset',
    '&.Mui-selected': {
      backgroundColor: 'rgba(236, 240, 245, 0)',
      color: '#2a2e49',
      borderBottom: 'none',
    },
    [theme.breakpoints.down('lg')]: {
      fontSize: '10px',
    },
  },
  tabContainer: {
    margin: '0 5px',
    minHeight: 'unset',
    flexShrink: 0,
  },
  indicator: {
    display: 'none',
  },
  shrink: {
    height: `calc(${theme.sizes.fullPage.height} - 85px)`,
  },
}));

export default ({ isOpen, style, toggleMapStyle, map, setIsInfoOpen, setCurrentItem, shrink }) => {
  const classes = useStyles();
  const [value, setValue] = useState(0);
  const { filters, variables, showEaWells } = useContext(MapFilterContext);

  const {
    loading: loadingEaWells,
    data: eaWellsData,
    refetch: refetchEaWells,
  } = useQuery(MAP_SCREENING_EA_WELLS_QUERY, {
    variables,
  });

  const handleEaWellsDataUpdate = (currentShowEaWells = showEaWells) => {
    const eaWellsSource = map?.getSource(MAP_SOURCE_IDS.eaWells);

    let timeout;
    if (!loadingEaWells && eaWellsData) {
      if (eaWellsSource) {
        const screeningPointFeatures = [];

        if (!eaWellsData?.mapScreeningEaWells || eaWellsData.mapScreeningEaWells.length === 0) {
          // If there are no E&A wells, we need to clear the source
          map.removeLayer(MAP_LAYER_IDS.eaWells);
          map.removeLayer(MAP_LAYER_IDS.eaWellLabels);
          return;
        }

        // Only update if we have actual data
        eaWellsData.mapScreeningEaWells?.forEach(a => {
          screeningPointFeatures.push({
            type: 'Feature',
            id: a.id,
            properties: {
              name: a.name,
              id: a.id,
              countryIsoCode: a.country.isoCode,
              country: a.country.name,
              entityType: a.entityType,
            },
            geometry: {
              type: 'Point',
              coordinates: getLatLongFromPoint(a.geom),
            },
          });
        });

        const newData = {
          type: 'FeatureCollection',
          features: screeningPointFeatures,
        };

        // Remove the layers that use the source first
        if (map.getLayer(MAP_LAYER_IDS.eaWells)) {
          map.removeLayer(MAP_LAYER_IDS.eaWells);
        }
        if (map.getLayer(MAP_LAYER_IDS.eaWellLabels)) {
          map.removeLayer(MAP_LAYER_IDS.eaWellLabels);
        }

        // Remove the existing source
        if (map.getSource(MAP_SOURCE_IDS.eaWells)) {
          map.removeSource(MAP_SOURCE_IDS.eaWells);
        }

        // Re-add the source with the new data
        map.addSource(MAP_SOURCE_IDS.eaWells, {
          type: 'geojson',
          data: newData,
        });

        // Wait a moment for the source to be ready
        setTimeout(() => {
          // Re-add the layers
          const eaWellLayerOptions = {
            ...MAP_LAYER_OPTIONS.eaWell,
            layout: {
              visibility: currentShowEaWells ? 'visible' : 'none', // Set based on current state
            },
          };
          map.addLayer(eaWellLayerOptions);

          const labelLayerOptions = {
            type: 'symbol',
            minzoom: 6,
            id: MAP_LAYER_IDS.eaWellLabels,
            source: MAP_SOURCE_IDS.eaWells,
            layout: {
              visibility: currentShowEaWells ? 'visible' : 'none', // Set based on current state
              'icon-anchor': 'center',
              'icon-allow-overlap': false,
              'icon-ignore-placement': false,
              'text-field': ['get', 'name'],
              'text-font': LABEL_FONT,
              'text-letter-spacing': 0.2,
              'text-size': 12,
              'text-anchor': 'center',
              'text-allow-overlap': false,
              'text-ignore-placement': false,
            },
            paint: {
              'text-halo-color': DEFAULT_BOUNDARY_COLOR,
              'text-halo-width': 1.5,
              'text-color': WELL_COLORS.wells_to_watch, // Yellow color for E&A well labels
            },
          };
          map.addLayer(labelLayerOptions);

          // move screening layers to the top
          map.moveLayer(MAP_LAYER_OPTIONS.screeningCluster.id);
          map.moveLayer(MAP_LAYER_OPTIONS.screeningClusterCount.id);
        }, 100);
      } else {
        // If the source doesn't exist, try again in 5 seconds:
        timeout = setTimeout(() => {
          handleEaWellsDataUpdate(currentShowEaWells);
        }, MAP_SOURCE_RETRY_TIMEOUT);
      }
    }

    return timeout;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Afraid to add handleEaWellsDataUpdate to deps and break something
  useEffect(() => {
    // Only update if we have actual data to avoid clearing the source
    if (eaWellsData?.mapScreeningEaWells) {
      const timeout = handleEaWellsDataUpdate(showEaWells);
      return () => clearTimeout(timeout);
    }
  }, [eaWellsData]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Refetch when filters change
  useEffect(() => {
    refetchEaWells(variables);
  }, [filters]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Call handleEaWellsDataUpdate when map is available to ensure layers are created
  useEffect(() => {
    if (map) {
      const timeout = handleEaWellsDataUpdate(showEaWells);
      return () => clearTimeout(timeout);
    }
  }, [map]);

  const handleChange = (_event, newValue) => {
    setValue(newValue);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: reload E&A wells when style changes
  useEffect(() => {
    map?.on('style.load', () => {
      handleEaWellsDataUpdate(showEaWells);
    });
  }, [style]);

  return (
    <Slide
      direction='right'
      in={isOpen}
      mountOnEnter
      unmountOnExit
      className={clsx(classes.filters, {
        [classes.shrink]: shrink,
      })}
    >
      <div>
        <FormControl component='fieldset' className={classes.formContainer}>
          <RadioGroup
            className={classes.radioGroup}
            aria-label='mapType'
            name='mapType'
            value={style}
            onChange={toggleMapStyle}
          >
            <FormControlLabel
              value={SATELLITE_MAP_STYLE.value}
              control={<Radio className={classes.radio} />}
              label={SATELLITE_MAP_STYLE.label}
            />
            <FormControlLabel
              value={DARK_MAP_STYLE.value}
              control={<Radio className={classes.radio} />}
              label={DARK_MAP_STYLE.label}
            />
            <FormControlLabel
              value={LIGHT_MAP_STYLE.value}
              control={<Radio className={classes.radio} />}
              label={LIGHT_MAP_STYLE.label}
            />
          </RadioGroup>
        </FormControl>
        <Tabs
          className={classes.tabContainer}
          TabIndicatorProps={{
            classes: {
              root: classes.indicator,
            },
          }}
          variant='fullWidth'
          scrollButtons='auto'
          value={value}
          onChange={handleChange}
        >
          {/* biome-ignore lint/correctness/useUniqueElementIds: Required for tab */}
          <Tab className={classes.tab} label='Search' id='search-filter-tab' />
          {/* biome-ignore lint/correctness/useUniqueElementIds: Required for tab */}
          <Tab className={classes.tab} label='Screening' id='screening-filter-tab' />
          {/* biome-ignore lint/correctness/useUniqueElementIds: Required for tab */}
          <Tab className={classes.tab} label='Layers' id='layers-filter-tab' />
        </Tabs>
        <ErrorBoundary>
          <MapSearchProvider>
            <SearchTab
              map={map}
              setCurrentItem={setCurrentItem}
              setIsInfoOpen={setIsInfoOpen}
              hidden={value !== 0}
            />
          </MapSearchProvider>
          <ScreeningTab map={map} hidden={value !== 1} />
          <LayersTab style={style} map={map} hidden={value !== 2} />
        </ErrorBoundary>
      </div>
    </Slide>
  );
};
